/**
 * History logging for migrations (no snapshots, no rollback).
 *
 * Each entry documents one applied step for one store.
 *
 * Shape (no enforced model):
 * {
 *   id: string,              // e.g., `${store}@${prevVersion}->${newVersion}@${ts}`
 *   store: string,
 *   prevVersion: string,     // version before applying this step
 *   newVersion: string,      // version applied for this step (the "chosen" version)
 *   bumpType: "major"|"minor"|"patch",
 *   appliedAt: number,       // epoch ms
 *   stepFile: string,        // cfg.filePath
 *   fileVersion: string|null,
 *   objectVersion: string,   // cfg.version
 *   decision: "migrate"|"skipped"|"blocked", // outcome
 *   reason?: string          // why skipped/blocked (optional)
 * }
 */

/**
 * appendHistory(db, entry)
 * Write a single migration history record into __history.
 * Non-throwing: logs errors but does not crash migrations.
 *
 * @param {Dexie} db
 * @param {Object} entry
 */
export async function appendHistory(db, entry) {
  try {
    const t = db.table("__history");
    const id =
      entry.id ||
      `${entry.store}@${entry.prevVersion}->${entry.newVersion}@${Date.now()}`;
    await t.put({ ...entry, id });
  } catch (err) {
    // Don’t block migrations if history fails; just report
    console.warn("[history] failed to write entry:", err);
  }
}

/**
 * deriveBumpType(prev, next) -> "major"|"minor"|"patch"
 * Given two triplets, classify the bump. Assumes next > prev.
 */
export function deriveBumpType(prev, next) {
  const [aA, aB, aC] = prev.split(".").map((n) => parseInt(n, 10));
  const [bA, bB, bC] = next.split(".").map((n) => parseInt(n, 10));
  if (bA > aA) return "major";
  if (bB > aB) return "minor";
  return "patch";
}
